<template>
   <div v-if="manager && messageKey">
      <ManagerMessage>
         <h2 class="card-title">{{ header }}</h2>
         <p class="card-description mb-0" v-html="description"></p>
         <div
            class="pt-3 d-flex align-items-center justify-content-end"
            v-if="hasUnlockButton || hasHomeButton"
         >
            <b-button
               v-if="hasUnlockButton"
               class="dark-button mr-2"
               style="font-size: 0.75rem"
               variant="primary"
               @click="unlock"
               >Unlock</b-button
            >
            <b-button
               v-if="hasHomeButton"
               variant="success"
               style="font-size: 0.75rem"
               :to="{name: 'Home'}"
               class="action-button ml-2"
            >
               Dashboard
            </b-button>
         </div>
      </ManagerMessage>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import ManagerMessage from '@/components/ManagerMessage.vue';
import {CROSS_PERIOD_MESSAGE_KEYS, CROSS_PERIOD_MESSAGE_CONFIG} from '@/helpers/baseConfig';
import {
   getRelevantFlags,
   hasUnresolvedFlags as hasUnresolvedExpenseFlags,
} from '@/helpers/expenseCompletion';
import {managerMessageMixin} from '@/mixins';

export default {
   name: 'CrossPeriodMessage',
   components: {
      ManagerMessage,
   },

   mixins: [managerMessageMixin],

   props: {
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },

   computed: {
      ...mapGetters({
         flags: 'flags/flags',
         activeStudyId: 'companies/activeStudyId',
         isRndig: 'isRndig',
      }),

      declaration() {
         return this.$store.getters[`${this.expenseType}/declaration`];
      },

      flagCheckSection() {
         return this.$store.getters[`${this.expenseType}/flagCheckSection`];
      },

      // Checked across every period, same scope as ExpensesCard.vue's own
      // hasUnresolvedFlags (which gates "Mark Section Complete") -- this message
      // spans the whole section too, not one period.
      hasUnresolvedFlags() {
         const relevantFlags = getRelevantFlags({
            flags: this.flags,
            studyId: this.activeStudyId,
            checkSection: this.flagCheckSection,
            userId: this.profile.id,
            includeAllUsers: this.isRndig,
         });

         return hasUnresolvedExpenseFlags(relevantFlags, this.$constants().FlagStates);
      },

      currentMessage() {
         return CROSS_PERIOD_MESSAGE_CONFIG[this.expenseType][this.messageKey];
      },

      header() {
         return this.currentMessage.header();
      },

      description() {
         return this.currentMessage.description();
      },

      hasInSingleUSStates() {
         return this.declaration === 'ONE';
      },

      hasInManyUSStates() {
         return this.declaration === 'MANY';
      },

      hasDeclarationByEntityType() {
         if (this.expenseType === 'supplies') {
            return this.hasInSingleUSStates || this.hasInManyUSStates;
         }

         if (this.expenseType === 'employees') {
            return true;
         }

         return this.declaration;
      },

      hasNoDeclaration() {
         if (this.expenseType === 'employees') {
            // Employees have no declaration concept -- `employees/declaration` is
            // permanently null (see store/modules/employees.js) since every company
            // has employees. Don't let that null short-circuit the message/button
            // logic below into the generic "no declaration" state.
            return false;
         }

         return this.declaration === null;
      },

      hasVendors() {
         return this.$store.getters[`${this.expenseType}/hasVendors`];
      },

      hasPeriodIds() {
         return this.periodIds.length !== 0;
      },

      assignedUsers() {
         return this.$store.getters[`${this.expenseType}/assignedUsers`];
      },

      hasAssignedUsers() {
         return this.assignedUsers.length !== 1 || this.assignedUsers[0] !== null;
      },

      // Both the persisted "assignee clicked Mark Assignment Complete" flag AND the
      // live merged status must read Complete -- a flag raised after completion
      // downgrades status without unsetting `completed`, and the message shown here
      // must not say "ready to mark complete"/"complete" while that's still true.
      otherAssignmentsAreComplete() {
         const others = this.sectionAssignments.filter(
            ({assigneeId}) => assigneeId !== this.profile.id
         );
         const othersMarkedComplete = !others.length || others.every(({completed}) => !!completed);
         const otherStatusesComplete = Object.entries(this.crossPeriodMergedStatuses)
            .filter(([assigneeId]) => assigneeId !== String(this.profile.id))
            .every(([, status]) => status === 'Complete');

         return othersMarkedComplete && otherStatusesComplete;
      },

      isCrossPeriodUnassignedComplete() {
         return (
            !this.crossPeriodMergedStatuses[null] ||
            this.crossPeriodMergedStatuses[null] === 'Complete'
         );
      },

      crossPeriodMergedStatuses() {
         return this.$store.getters[`${this.expenseType}/crossPeriodMergedStatuses`];
      },

      isReadyToBeMarkedComplete() {
         return this.areAllAssignmentsComplete && this.isCrossPeriodUnassignedComplete;
      },

      // A user with no assignments of their own has nothing to "need input" on --
      // don't let them see that message just because myAssignmentsAreComplete is
      // (correctly) false for them; route them straight to the others/ready checks.
      hasAssignmentsInSection() {
         return this.sectionAssignments.some(
            ({assigneeId}) => String(assigneeId ?? null) === String(this.profile.id ?? null)
         );
      },

      // See otherAssignmentsAreComplete's comment -- same reasoning for self.
      myAssignmentsAreComplete() {
         const matchingAssignments = this.sectionAssignments.filter(
            ({assigneeId}) => String(assigneeId ?? null) === String(this.profile.id ?? null)
         );
         const markedComplete =
            matchingAssignments.length > 0 &&
            matchingAssignments.every(({completed}) => !!completed);

         return markedComplete && this.crossPeriodMergedStatuses[this.profile.id] === 'Complete';
      },

      smeMessageKey() {
         const {SME_NEEDS_INPUT, SME_WAITING_ON_OTHERS, SME_DONE} = CROSS_PERIOD_MESSAGE_KEYS;

         if (this.isCompleted) return SME_DONE;
         if (this.hasAssignmentsInSection && !this.myAssignmentsAreComplete) return SME_NEEDS_INPUT;

         return SME_WAITING_ON_OTHERS;
      },

      customerMessageKey() {
         const {
            CUSTOMER_NO_VENDORS,
            CUSTOMER_ASSIGN_VENDORS,
            CUSTOMER_NEEDS_INPUT,
            CUSTOMER_WAITING_ON_OTHERS,
            CUSTOMER_READY_TO_MARK_COMPLETE,
            CUSTOMER_SECTION_COMPLETE,
         } = CROSS_PERIOD_MESSAGE_KEYS;

         if (this.isCompleted) return CUSTOMER_SECTION_COMPLETE;
         if (!this.hasVendors) return CUSTOMER_NO_VENDORS;
         if (this.hasPeriodIds && !this.hasAssignedUsers) return CUSTOMER_ASSIGN_VENDORS;
         if (this.hasAssignmentsInSection && !this.myAssignmentsAreComplete) {
            return CUSTOMER_NEEDS_INPUT;
         }
         if (!this.otherAssignmentsAreComplete || !this.isCrossPeriodUnassignedComplete) {
            return CUSTOMER_WAITING_ON_OTHERS;
         }
         if (this.isReadyToBeMarkedComplete) return CUSTOMER_READY_TO_MARK_COMPLETE;

         return CUSTOMER_NEEDS_INPUT;
      },

      messageKey() {
         const {FLAGS, NO_DECLARATION, SECTION_SKIPPED} = CROSS_PERIOD_MESSAGE_KEYS;

         if (this.hasUnresolvedFlags) return FLAGS;
         if (this.hasNoDeclaration) return NO_DECLARATION;
         if (!this.hasDeclarationByEntityType) return SECTION_SKIPPED;
         if (this.isSME) return this.smeMessageKey;
         if (this.isCustomer) return this.customerMessageKey;

         return null;
      },

      hasHomeButton() {
         const {
            SECTION_SKIPPED,
            CUSTOMER_SECTION_COMPLETE,
            CUSTOMER_WAITING_ON_OTHERS,
            SME_WAITING_ON_OTHERS,
            SME_DONE,
         } = CROSS_PERIOD_MESSAGE_KEYS;

         return [
            SECTION_SKIPPED,
            CUSTOMER_SECTION_COMPLETE,
            CUSTOMER_WAITING_ON_OTHERS,
            SME_WAITING_ON_OTHERS,
            SME_DONE,
         ].includes(this.messageKey);
      },

      hasUnlockButton() {
         const {CUSTOMER_SECTION_COMPLETE} = CROSS_PERIOD_MESSAGE_KEYS;
         return this.messageKey === CUSTOMER_SECTION_COMPLETE;
      },
   },
};
</script>

<style lang="scss" scoped>
.card-title {
   font-size: 1.25rem;
   font-weight: 700;
}

.card-description {
   font-size: 0.8125rem;
}
</style>
